import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Footer badge showing the currently-deployed build.
 *
 * Renders like: ``v1.0.0 · 200620b · 31 Jul 07:47``
 *
 * Hits the public ``/api/version`` endpoint once on mount. If the fetch
 * fails (e.g. backend down), the badge silently hides — no reason to
 * clutter the UI over a diagnostic surface.
 *
 * Uses a small tooltip with the full commit SHA + committed_at timestamp
 * on hover so support can grab both without digging into the response
 * body.
 */
export function VersionFooter() {
  const [v, setV] = useState(null);
  useEffect(() => {
    let mounted = true;
    api.get("/version")
      .then(({ data }) => { if (mounted) setV(data); })
      .catch(() => { /* silent — footer stays hidden */ });
    return () => { mounted = false; };
  }, []);

  if (!v) return null;
  const shortSha = (v.commit || "").slice(0, 7);
  const when = v.committed_at
    ? new Date(v.committed_at).toLocaleString(undefined, {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : "";
  const tooltip = `Version ${v.version}\nCommit ${v.commit}${when ? "\nCommitted " + when : ""}`;
  return (
    <div
      data-testid="version-footer"
      title={tooltip}
      className="px-3 py-2 text-[10px] text-slate-600 border-t border-slate-800/60 font-mono tracking-wide select-none cursor-help hover:text-slate-400 transition-colors"
    >
      v{v.version} · {shortSha}{when ? ` · ${when}` : ""}
    </div>
  );
}
